import { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

const Root = styled(Box)(() => ({ display: "block", lineHeight: 0 }));

type SparklineProps = {
  values: number[];
  height?: number;
  /** Semantic colour is reserved for status; a sparkline is neutral. */
  color?: string;
};

/**
 * A recorded series, drawn to its own scale.
 *
 * Deliberately unlabelled and unaxed: this shows the shape of what was
 * recorded, not a judgment about it. No thresholds, no bands, no colour that
 * would imply a reading is good or bad. Measures its container and draws at
 * that pixel width so strokes stay crisp. (Ported from the mission-agent
 * dashboard; plain SVG maths instead of d3.)
 */
export function Sparkline(props: SparklineProps) {
  const holder = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const height = props.height ?? 28;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = holder.current;
    if (!element) return undefined;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const stroke = props.color ?? theme.palette.text.secondary;
  const values = props.values;
  let path = "";
  let end: [number, number] | null = null;
  if (values.length >= 2 && width >= 8) {
    const padding = 3;
    const min = Math.min(...values);
    const max = Math.max(...values);
    // A flat series would collapse to a zero-height scale and disappear.
    const span = max - min || 1;
    const lo = min - span * 0.1;
    const hi = max + span * 0.1;
    const x = (i: number) => padding + (i * (width - 2 * padding)) / (values.length - 1);
    const y = (v: number) => height - padding - ((v - lo) * (height - 2 * padding)) / (hi - lo);
    path = values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
    end = [x(values.length - 1), y(values[values.length - 1])];
  }

  return (
    <Root ref={holder}>
      <svg
        height={height}
        style={{ display: "block", width: "100%" }}
        role="img"
        aria-label={values.length ? `Trend of ${values.length} readings, latest ${values[values.length - 1]}` : "No readings"}
      >
        {path ? (
          <>
            <path
              d={path}
              fill="none"
              stroke={stroke}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* Emphasise the latest point: the current value is what is being read. */}
            {end ? <circle cx={end[0]} cy={end[1]} r={2.2} fill={stroke} /> : null}
          </>
        ) : null}
      </svg>
    </Root>
  );
}
